import { UseSearchParamsParameters } from '@occc-app/hooks/useSearchParams';
import { Weekday } from '@occc/graphql';

// =============================================================================
// ROUTES
// =============================================================================

export const TOUCHPOINT_BASE_URL = '/touchpoint';
export const TOUCHPOINT_AD_SETS_BASE_URL = `${TOUCHPOINT_BASE_URL}/ad-sets`;
export const TOUCHPOINT_ASSETS_BASE_URL = `${TOUCHPOINT_BASE_URL}/assets`;
export const TOUCHPOINT_DEFAULT_SCREENS_BASE_URL = `${TOUCHPOINT_BASE_URL}/default-screen`;
export const TOUCHPOINT_SCHEDULE_BASE_URL = `${TOUCHPOINT_BASE_URL}/schedule`;

// =============================================================================
// VALIDATION LIMITS
// =============================================================================

export const MAX_FILE_SIZE_MB = 20;
export const MAX_ASSET_NAME_LENGTH = 200;
export const MAX_ASSET_DESCRIPTION_LENGTH = 200;
export const MAX_DEFAULT_SCREEN_NAME_LENGTH = 500;
export const MAX_DEFAULT_SCREEN_DESCRIPTION_LENGTH = 500;

// =============================================================================
// IMAGE ASPECT RATIOS
// =============================================================================

export type AspectRatio = '4x3' | '1x1' | '9x16';

export const aspectRatioDimensions: Record<
  AspectRatio,
  { width: number; height: number; size: string }
> = {
  '4x3': {
    width: 1600,
    height: 1200,
    size: '500 KB',
  },
  '1x1': {
    width: 1080,
    height: 1080,
    size: '500 KB',
  },
  '9x16': {
    width: 1080,
    height: 1920,
    size: '500 KB',
  },
};

// =============================================================================
// SHARED UI TEXT
// =============================================================================

export const CLOSE_BUTTON_TEXT = 'Close';
export const CANCEL_BUTTON_TEXT = 'Cancel';
export const SAVE_BUTTON_TEXT = 'Save';

// =============================================================================
// AD SETS
// =============================================================================

export const AD_SET_CREATE_SUCCESS_MESSAGE = 'The ad set was successfully created';
export const AD_SET_CREATE_ERROR_MESSAGE = 'Failed to create the ad set. Please try again later';
export const AD_SET_UPDATE_SUCCESS_MESSAGE = 'The ad set was successfully updated';
export const AD_SET_UPDATE_ERROR_MESSAGE = 'Failed to update the ad set. Please try again later';
export const AD_SET_DELETE_SUCCESS_MESSAGE = 'The ad set was successfully deleted';
export const AD_SET_DELETE_ERROR_MESSAGE = 'Failed to delete the ad set. Please try again later';
export const AD_SET_PUBLISH_SUCCESS_MESSAGE = 'The ad set was successfully published';
export const AD_SET_PUBLISH_ERROR_MESSAGE = 'Failed to publish the ad set. Please try again later';
export const AD_SET_ARCHIVE_SUCCESS_MESSAGE = 'The ad set was successfully archived';
export const AD_SET_ARCHIVE_ERROR_MESSAGE = 'Failed to archive the ad set. Please try again later';

export const AD_SET_SYNC_INFO_MESSAGE =
  'Changes will be displayed on the touchpoint only after the next synchronization (once per hour).';
export const ASSET_CONFIRMATION_MESSAGE =
  "Changes to this Asset will be applied to all Ad Sets where it is used.\nIf you don't want to affect existing Ad Sets, create a new Asset instead.\nDo you still want to save these changes?";

export const SELECT_TARGET_SECTION_TITLE = 'Select Target';
export const SELECT_TARGET_SECTION_SUBTITLE =
  'Add Terminal/Kiosk IDs to define where ads are shown.';
export const TERMINAL_KIOSK_IDS_FIELD_LABEL = 'Terminal/Kiosk IDs';
export const TARGETED_KIOSKS_SECTION_TITLE = 'Targeted Kiosks';
export const KIOSK_COLUMN_LABEL = 'Kiosk';
export const KIOSK_ADDRESS_COLUMN_LABEL = 'Kiosk Address';
export const KIOSK_ID_COLUMN_LABEL = 'Kiosk ID';
export const PHONE_NUMBER_COLUMN_LABEL = 'Phone Number';
export const ADD_TARGET_BUTTON_TEXT = 'Add Target';
export const SELECT_TARGET_KIOSKS_TITLE = 'Select target';
export const AD_TERMINAL_KIOSK_IDS = 'Ad Terminal/Kiosk IDs';

// =============================================================================
// ASSETS
// =============================================================================

export const ASSET_CREATE_SUCCESS_MESSAGE = 'The asset was successfully created';
export const ASSET_CREATE_ERROR_MESSAGE = 'Failed to create the asset. Please try again later';
export const ASSET_UPDATE_SUCCESS_MESSAGE = 'The asset was successfully updated';
export const ASSET_UPDATE_ERROR_MESSAGE = 'Failed to update the asset. Please try again later';
export const ASSET_DELETE_SUCCESS_MESSAGE = 'The asset was successfully deleted';
export const ASSET_DELETE_ERROR_MESSAGE = 'Failed to delete the asset. Please try again later';
export const ASSET_UPLOAD_ERROR_MESSAGE = 'An error occurred while uploading the file';

// =============================================================================
// DEFAULT SCREENS
// =============================================================================

// Page UI
export const DEFAULT_SCREENS = 'Default Screens';
export const DEFAULT_SCREENS_PAGE_TITLE = 'Default Screens';
export const DEFAULT_SCREENS_PAGE_SUBTITLE =
  'Use this section to upload fallback images that will appear when no ad is scheduled.';
export const DEFAULT_SCREENS_ADD_NEW_DEFAULT_SCREEN_BUTTON_TEXT = 'Add New Default Image';
export const DEFAULT_SCREENS_TABLE_TITLE = 'Default Screen Images';

// Details Page
export const DEFAULT_SCREEN_DETAILS_PAGE_TITLE = 'Default Screen Details';
export const DEFAULT_SCREEN_DETAILS_LOADING_SUBTITLE = 'Loading...';
export const DEFAULT_SCREEN_DETAILS_ERROR_SUBTITLE = 'Error';
export const DEFAULT_SCREEN_DETAILS_IMAGES_TITLE = 'Default Screen Images';
export const DEFAULT_SCREEN_NOT_FOUND_MESSAGE = 'Default screen not found';
export const DEFAULT_SCREEN_DETAILS_BACK_BUTTON_TEXT = 'Back to Default Screens';
export const DEFAULT_SCREEN_DELETE_FAILED_MESSAGE_PREFIX = 'Failed to delete default screen: ';

// Form Labels
export const DEFAULT_SCREEN_DETAILS_SECTION_TITLE = 'Default Screen Details';
export const DEFAULT_SCREEN_IMAGES_SECTION_TITLE = 'Default Screen Images';
export const DEFAULT_SCREEN_NAME_LABEL = 'Default Screen Name';
export const DEFAULT_SCREEN_DESCRIPTION_LABEL = 'Default Screen Description';
export const DEFAULT_SCREEN_4X3_IMAGE_LABEL = '4x3 image';
export const DEFAULT_SCREEN_1X1_IMAGE_LABEL = '1x1 image';
export const DEFAULT_SCREEN_9X16_IMAGE_LABEL = '9x16 image (for Android app)';

// Messages
export const DEFAULT_SCREEN_CREATE_SUCCESS_MESSAGE = 'The default screen was successfully created';
export const DEFAULT_SCREEN_CREATE_ERROR_MESSAGE =
  'Failed to create the default screen. Please try again later';
export const DEFAULT_SCREEN_UPDATE_SUCCESS_MESSAGE = 'The default screen was successfully updated';
export const DEFAULT_SCREEN_UPDATE_ERROR_MESSAGE =
  'Failed to update the default screen. Please try again later';
export const DEFAULT_SCREEN_DELETE_SUCCESS_MESSAGE = 'The default screen was successfully deleted';
export const DEFAULT_SCREEN_DELETE_ERROR_MESSAGE =
  'Failed to delete the default screen. Please try again later';

// Search & Pagination
const DEFAULT_SCREENS_BEFORE_PARAM = 'default-screens-before';
const DEFAULT_SCREENS_AFTER_PARAM = 'default-screens-after';

export const DEFAULT_SCREENS_SEARCH_PARAMS: UseSearchParamsParameters = {
  searchParamName: 'default-screens-search',
  beforeParamName: DEFAULT_SCREENS_BEFORE_PARAM,
  afterParamName: DEFAULT_SCREENS_AFTER_PARAM,
};

export const DEFAULT_SCREEN_PAGINATION_PARAMS = {
  beforeParamName: DEFAULT_SCREENS_BEFORE_PARAM,
  afterParamName: DEFAULT_SCREENS_AFTER_PARAM,
};

// =============================================================================
// SCHEDULE
// =============================================================================

export const SCHEDULE_PAGE_TITLE = 'Schedule';
export const SCHEDULE_PAGE_SUBTITLE =
  'Define when your ads will run by setting active time frames and selecting specific weekdays. Use this page to control the timing and frequency of ad display in stores.';
export const SCHEDULE_CALENDAR_TITLE = 'Ad Sets Schedule';

const SCHEDULE_BEFORE_PARAM = 'schedule-before';
const SCHEDULE_AFTER_PARAM = 'schedule-after';

export const SCHEDULE_SEARCH_PARAMS: UseSearchParamsParameters = {
  searchParamName: 'schedule-search',
  beforeParamName: SCHEDULE_BEFORE_PARAM,
  afterParamName: SCHEDULE_AFTER_PARAM,
};

export const SCHEDULE_PAGINATION_PARAMS = {
  beforeParamName: SCHEDULE_BEFORE_PARAM,
  afterParamName: SCHEDULE_AFTER_PARAM,
};

// =============================================================================
// WEEKDAYS
// =============================================================================

export const WEEKDAYS = [
  { label: 'Monday', value: Weekday.Monday },
  { label: 'Tuesday', value: Weekday.Tuesday },
  { label: 'Wednesday', value: Weekday.Wednesday },
  { label: 'Thursday', value: Weekday.Thursday },
  { label: 'Friday', value: Weekday.Friday },
  { label: 'Saturday', value: Weekday.Saturday },
  { label: 'Sunday', value: Weekday.Sunday },
];

export const SYSTEM_WEEKDAYS = [
  Weekday.Sunday,
  Weekday.Monday,
  Weekday.Tuesday,
  Weekday.Wednesday,
  Weekday.Thursday,
  Weekday.Friday,
  Weekday.Saturday,
];

// =============================================================================
// TABLE STYLES
// =============================================================================

export const TABLE_CELL_TRUNCATE_STYLES = {
  maxWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const TABLE_CELL_IMAGE_STYLES = {
  display: 'flex',
  justifyContent: 'flex-end',
} as const;
